import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Shield,
  Target,
  AlertTriangle,
  Check,
  Plus,
  Trash2,
  Users,
  Award,
  UserCheck,
  Send,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { getMatch, getTeams, getPlayers, updateMatch, getAvailableOfficials, assignRefereesToMatch } from "../lib/api";
import { toast } from "sonner";

export default function MatchEntry() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [homeGoals, setHomeGoals] = useState([]);
  const [awayGoals, setAwayGoals] = useState([]);
  const [cards, setCards] = useState([]);
  const [homePlayersPresent, setHomePlayersPresent] = useState([]);
  const [awayPlayersPresent, setAwayPlayersPresent] = useState([]);
  const [homeMvp, setHomeMvp] = useState("");
  const [awayMvp, setAwayMvp] = useState("");
  const [refereeAssignments, setRefereeAssignments] = useState([]);
  const [venue, setVenue] = useState("");

  useEffect(() => {
    fetchData();
  }, [matchId]);

  const fetchData = async () => {
    try {
      const [matchRes, teamsRes, playersRes] = await Promise.all([
        getMatch(matchId),
        getTeams(),
        getPlayers(),
      ]);
      const matchData = matchRes.data;
      setMatch(matchData);
      setTeams(teamsRes.data);
      setPlayers(playersRes.data);

      // Fetch available officials (all referees and delegates)
      try {
        const officialsRes = await getAvailableOfficials();
        setReferees(officialsRes.data);
      } catch (e) {
        setReferees([]);
      }

      setHomeScore(matchData.home_score || 0);
      setAwayScore(matchData.away_score || 0);
      setHomeGoals(matchData.home_goals || []);
      setAwayGoals(matchData.away_goals || []);
      setCards(matchData.cards || []);
      setHomePlayersPresent(matchData.home_players_present || []);
      setAwayPlayersPresent(matchData.away_players_present || []);
      setHomeMvp(matchData.home_mvp || "");
      setAwayMvp(matchData.away_mvp || "");
      setRefereeAssignments(matchData.referees || []);
      setVenue(matchData.venue || "");
    } catch (error) {
      toast.error("Failed to load match data");
      navigate("/dashboard/matches");
    } finally {
      setLoading(false);
    }
  };

  const homeTeam = teams.find((t) => t.id === match?.home_team_id);
  const awayTeam = teams.find((t) => t.id === match?.away_team_id);
  const homePlayers = players.filter((p) => p.team_id === match?.home_team_id);
  const awayPlayers = players.filter((p) => p.team_id === match?.away_team_id);

  const togglePlayerPresent = (playerId, isHome) => {
    if (isHome) {
      setHomePlayersPresent((prev) =>
        prev.includes(playerId)
          ? prev.filter((id) => id !== playerId)
          : [...prev, playerId]
      );
    } else {
      setAwayPlayersPresent((prev) =>
        prev.includes(playerId)
          ? prev.filter((id) => id !== playerId)
          : [...prev, playerId]
      );
    }
  };

  const selectAllPlayers = (isHome) => {
    if (isHome) {
      setHomePlayersPresent(homePlayers.map((p) => p.id));
    } else {
      setAwayPlayersPresent(awayPlayers.map((p) => p.id));
    }
  };

  const deselectAllPlayers = (isHome) => {
    if (isHome) {
      setHomePlayersPresent([]);
    } else {
      setAwayPlayersPresent([]);
    }
  };

  const addGoal = (isHome) => {
    const newGoal = { player_id: "", assist_player_id: "", minute: 0 };
    if (isHome) {
      setHomeGoals([...homeGoals, newGoal]);
    } else {
      setAwayGoals([...awayGoals, newGoal]);
    }
  };

  const updateGoal = (isHome, index, field, value) => {
    const goals = isHome ? [...homeGoals] : [...awayGoals];
    goals[index] = { ...goals[index], [field]: value };
    if (isHome) {
      setHomeGoals(goals);
    } else {
      setAwayGoals(goals);
    }
  };

  const removeGoal = (isHome, index) => {
    if (isHome) {
      setHomeGoals(homeGoals.filter((_, i) => i !== index));
    } else {
      setAwayGoals(awayGoals.filter((_, i) => i !== index));
    }
  };

  const addCard = () => {
    setCards([...cards, { player_id: "", card_type: "yellow", minute: 0 }]);
  };

  const updateCard = (index, field, value) => {
    const newCards = [...cards];
    newCards[index] = { ...newCards[index], [field]: value };
    
    // Check for automatic red card (2 yellows = red)
    if (field === "player_id" || field === "card_type") {
      const updatedCard = newCards[index];
      if (updatedCard.player_id && updatedCard.card_type === "yellow") {
        // Count yellow cards for this player (excluding current card being edited)
        const yellowCount = newCards.filter(
          (c, i) => i !== index && c.player_id === updatedCard.player_id && c.card_type === "yellow"
        ).length;
        
        // If player already has 1 yellow, this is their 2nd - auto add red
        if (yellowCount >= 1) {
          const hasRed = newCards.some(
            c => c.player_id === updatedCard.player_id && c.card_type === "red"
          );
          if (!hasRed) {
            // Add automatic red card
            newCards.push({
              player_id: updatedCard.player_id,
              card_type: "red",
              minute: updatedCard.minute,
              auto_generated: true
            });
            toast.info("Second yellow card - Red card automatically added!");
          }
        }
      }
    }
    
    setCards(newCards);
  };

  const removeCard = (index) => {
    const cardToRemove = cards[index];
    let newCards = cards.filter((_, i) => i !== index);
    
    // If removing a yellow card, check if we should remove auto-generated red
    if (cardToRemove.card_type === "yellow" && cardToRemove.player_id) {
      const remainingYellows = newCards.filter(
        c => c.player_id === cardToRemove.player_id && c.card_type === "yellow"
      ).length;
      
      // If less than 2 yellows remain, remove auto-generated red card
      if (remainingYellows < 2) {
        newCards = newCards.filter(
          c => !(c.player_id === cardToRemove.player_id && c.card_type === "red" && c.auto_generated)
        );
      }
    }
    
    setCards(newCards);
  };

  // Referee assignment functions
  const addRefereeAssignment = () => {
    setRefereeAssignments([...refereeAssignments, { referee_id: "", role: "" }]);
  };

  const updateRefereeAssignment = (index, field, value) => {
    const newAssignments = [...refereeAssignments];
    newAssignments[index] = { ...newAssignments[index], [field]: value };
    // Clear referee_id when role changes (user must re-select from filtered list)
    if (field === "role") {
      newAssignments[index].referee_id = "";
    }
    setRefereeAssignments(newAssignments);
  };

  // Filter officials based on selected role AND exclude officials from teams playing in this match
  const getFilteredOfficials = (role) => {
    if (!role) return [];
    return referees.filter(official => {
      // Exclude officials from teams playing in this match
      if (official.team_id === match?.home_team_id || official.team_id === match?.away_team_id) {
        return false;
      }
      
      if (role === "Delegate") {
        // Delegates can only be players marked as delegate
        return official.is_delegate;
      } else {
        // Center Referee and Assistant can only be players marked as referee
        return official.is_referee;
      }
    });
  };

  const removeRefereeAssignment = (index) => {
    setRefereeAssignments(refereeAssignments.filter((_, i) => i !== index));
  };

  const handleAssignReferees = async (sendNotifications = true) => {
    const validAssignments = refereeAssignments.filter(a => a.referee_id);
    if (validAssignments.length === 0) {
      toast.error("Please add at least one referee assignment");
      return;
    }

    setSaving(true);
    try {
      const res = await assignRefereesToMatch(matchId, validAssignments, sendNotifications);
      
      // Show notification results
      const notifications = res.data.notifications || [];
      const sent = notifications.filter(n => n.notification_sent).length;
      const failed = notifications.filter(n => !n.notification_sent).length;
      
      if (sent > 0) {
        toast.success(`Referees assigned! ${sent} notification(s) sent.`);
      } else if (failed > 0) {
        toast.warning("Referees assigned, but notifications failed. Check email settings.");
      } else {
        toast.success("Referees assigned successfully!");
      }
      
      fetchData();
    } catch (error) {
      toast.error("Failed to assign referees");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (complete = false) => {
    setSaving(true);
    try {
      const validHomeGoals = homeGoals.filter((g) => g.player_id);
      const validAwayGoals = awayGoals.filter((g) => g.player_id);
      const validCards = cards.filter((c) => c.player_id);

      const data = {
        home_score: parseInt(homeScore) || 0,
        away_score: parseInt(awayScore) || 0,
        home_goals: validHomeGoals,
        away_goals: validAwayGoals,
        cards: validCards,
        home_players_present: homePlayersPresent,
        away_players_present: awayPlayersPresent,
        home_mvp: homeMvp || null,
        away_mvp: awayMvp || null,
        venue: venue || null,
        status: complete ? "completed" : "scheduled",
      };

      await updateMatch(matchId, data);
      toast.success(complete ? "Match completed!" : "Match saved");

      if (complete) {
        navigate("/dashboard/matches");
      } else {
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to save match");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!match) return null;

  // Get only present players for goal/card selection
  const presentHomePlayers = homePlayers.filter((p) => homePlayersPresent.includes(p.id));
  const presentAwayPlayers = awayPlayers.filter((p) => awayPlayersPresent.includes(p.id));

  return (
    <div data-testid="match-entry-page" className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard/matches")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
            Match Entry
          </h1>
          <p className="text-muted-foreground">
            {match.match_date} • {match.match_time}
          </p>
        </div>
      </div>

      {/* Score Card */}
      <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between gap-6">
          {/* Home Team */}
          <div className="flex-1 text-center">
            {homeTeam?.logo ? (
              <img
                src={homeTeam.logo}
                alt={homeTeam.name}
                className="w-20 h-20 md:w-24 md:h-24 mx-auto rounded-xl object-cover mb-3"
              />
            ) : (
              <div className="w-20 h-20 md:w-24 md:h-24 mx-auto bg-secondary rounded-xl flex items-center justify-center mb-3">
                <Shield className="w-10 h-10 text-muted-foreground" />
              </div>
            )}
            <h3 className="font-heading text-lg md:text-xl font-bold uppercase">
              {homeTeam?.name || "Home"}
            </h3>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Home
            </p>
          </div>

          {/* Score Input */}
          <div className="flex items-center gap-4">
            <Input
              type="number"
              min="0"
              value={homeScore}
              onChange={(e) => setHomeScore(e.target.value)}
              className="w-20 h-20 text-center text-4xl font-heading font-black bg-background border-border score-display"
              data-testid="home-score-input"
            />
            <span className="text-3xl text-muted-foreground font-heading">
              -
            </span>
            <Input
              type="number"
              min="0"
              value={awayScore}
              onChange={(e) => setAwayScore(e.target.value)}
              className="w-20 h-20 text-center text-4xl font-heading font-black bg-background border-border score-display"
              data-testid="away-score-input"
            />
          </div>

          {/* Away Team */}
          <div className="flex-1 text-center">
            {awayTeam?.logo ? (
              <img
                src={awayTeam.logo}
                alt={awayTeam.name}
                className="w-20 h-20 md:w-24 md:h-24 mx-auto rounded-xl object-cover mb-3"
              />
            ) : (
              <div className="w-20 h-20 md:w-24 md:h-24 mx-auto bg-secondary rounded-xl flex items-center justify-center mb-3">
                <Shield className="w-10 h-10 text-muted-foreground" />
              </div>
            )}
            <h3 className="font-heading text-lg md:text-xl font-bold uppercase">
              {awayTeam?.name || "Away"}
            </h3>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Away
            </p>
          </div>
        </div>
      </div>

      {/* Player Attendance Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Home Team Attendance */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg font-bold uppercase">
                {homeTeam?.name} Attendance
              </h3>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => selectAllPlayers(true)}
                className="text-xs"
                data-testid="select-all-home"
              >
                All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deselectAllPlayers(true)}
                className="text-xs"
                data-testid="deselect-all-home"
              >
                None
              </Button>
            </div>
          </div>

          {homePlayers.length > 0 ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {homePlayers.map((player) => (
                <label
                  key={player.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 cursor-pointer"
                  data-testid={`home-player-${player.id}`}
                >
                  <Checkbox
                    checked={homePlayersPresent.includes(player.id)}
                    onCheckedChange={() => togglePlayerPresent(player.id, true)}
                  />
                  <span className="flex-1">{player.name}</span>
                  {player.number && (
                    <span className="text-xs text-muted-foreground">#{player.number}</span>
                  )}
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No players registered
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            {homePlayersPresent.length} of {homePlayers.length} present
          </p>
        </div>

        {/* Away Team Attendance */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-accent" />
              <h3 className="font-heading text-lg font-bold uppercase">
                {awayTeam?.name} Attendance
              </h3>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => selectAllPlayers(false)}
                className="text-xs"
                data-testid="select-all-away"
              >
                All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deselectAllPlayers(false)}
                className="text-xs"
                data-testid="deselect-all-away"
              >
                None
              </Button>
            </div>
          </div>

          {awayPlayers.length > 0 ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {awayPlayers.map((player) => (
                <label
                  key={player.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50 cursor-pointer"
                  data-testid={`away-player-${player.id}`}
                >
                  <Checkbox
                    checked={awayPlayersPresent.includes(player.id)}
                    onCheckedChange={() => togglePlayerPresent(player.id, false)}
                  />
                  <span className="flex-1">{player.name}</span>
                  {player.number && (
                    <span className="text-xs text-muted-foreground">#{player.number}</span>
                  )}
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No players registered
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            {awayPlayersPresent.length} of {awayPlayers.length} present
          </p>
        </div>
      </div>

      {/* MVP Selection */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Home Team MVP */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-amber-500" />
            <h3 className="font-heading text-lg font-bold uppercase">
              {homeTeam?.name} MVP
            </h3>
          </div>
          <Select
            value={homeMvp || "none"}
            onValueChange={(v) => setHomeMvp(v === "none" ? "" : v)}
          >
            <SelectTrigger
              className="bg-background border-border"
              data-testid="home-mvp-select"
            >
              <SelectValue placeholder="Select MVP" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="none">No MVP Selected</SelectItem>
              {(presentHomePlayers.length > 0 ? presentHomePlayers : homePlayers).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.number ? `${p.number}. ` : ""}{p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Away Team MVP */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-amber-500" />
            <h3 className="font-heading text-lg font-bold uppercase">
              {awayTeam?.name} MVP
            </h3>
          </div>
          <Select
            value={awayMvp || "none"}
            onValueChange={(v) => setAwayMvp(v === "none" ? "" : v)}
          >
            <SelectTrigger
              className="bg-background border-border"
              data-testid="away-mvp-select"
            >
              <SelectValue placeholder="Select MVP" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="none">No MVP Selected</SelectItem>
              {(presentAwayPlayers.length > 0 ? presentAwayPlayers : awayPlayers).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.number ? `${p.number}. ` : ""}{p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Goals Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Home Goals */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              <h3 className="font-heading text-lg font-bold uppercase">
                {homeTeam?.name} Goals
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => addGoal(true)}
              className="text-primary"
              data-testid="add-home-goal-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          {homeGoals.length > 0 ? (
            <div className="space-y-4">
              {homeGoals.map((goal, index) => (
                <div
                  key={index}
                  className="p-4 bg-background rounded-lg space-y-3"
                  data-testid={`home-goal-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase text-muted-foreground">
                      Goal {index + 1}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeGoal(true, index)}
                      className="text-destructive h-6 w-6 p-0"
                      data-testid={`remove-home-goal-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Select
                    value={goal.player_id}
                    onValueChange={(v) => updateGoal(true, index, "player_id", v)}
                  >
                    <SelectTrigger
                      className="bg-card border-border"
                      data-testid={`home-goal-scorer-${index}`}
                    >
                      <SelectValue placeholder="Goal Scorer" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {(presentHomePlayers.length > 0 ? presentHomePlayers : homePlayers).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.number ? `${p.number}. ` : ""}{p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={goal.assist_player_id || "none"}
                    onValueChange={(v) =>
                      updateGoal(true, index, "assist_player_id", v === "none" ? "" : v)
                    }
                  >
                    <SelectTrigger
                      className="bg-card border-border"
                      data-testid={`home-goal-assist-${index}`}
                    >
                      <SelectValue placeholder="Assist (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="none">No Assist</SelectItem>
                      {(presentHomePlayers.length > 0 ? presentHomePlayers : homePlayers)
                        .filter((p) => p.id !== goal.player_id)
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.number ? `${p.number}. ` : ""}{p.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground w-16">
                      Minute
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="120"
                      value={goal.minute}
                      onChange={(e) =>
                        updateGoal(true, index, "minute", parseInt(e.target.value) || 0)
                      }
                      className="bg-card border-border w-20"
                      data-testid={`home-goal-minute-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No goals recorded
            </p>
          )}
        </div>

        {/* Away Goals */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-accent" />
              <h3 className="font-heading text-lg font-bold uppercase">
                {awayTeam?.name} Goals
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => addGoal(false)}
              className="text-accent"
              data-testid="add-away-goal-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          {awayGoals.length > 0 ? (
            <div className="space-y-4">
              {awayGoals.map((goal, index) => (
                <div
                  key={index}
                  className="p-4 bg-background rounded-lg space-y-3"
                  data-testid={`away-goal-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase text-muted-foreground">
                      Goal {index + 1}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeGoal(false, index)}
                      className="text-destructive h-6 w-6 p-0"
                      data-testid={`remove-away-goal-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Select
                    value={goal.player_id}
                    onValueChange={(v) => updateGoal(false, index, "player_id", v)}
                  >
                    <SelectTrigger
                      className="bg-card border-border"
                      data-testid={`away-goal-scorer-${index}`}
                    >
                      <SelectValue placeholder="Goal Scorer" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {(presentAwayPlayers.length > 0 ? presentAwayPlayers : awayPlayers).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.number ? `${p.number}. ` : ""}{p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={goal.assist_player_id || "none"}
                    onValueChange={(v) =>
                      updateGoal(false, index, "assist_player_id", v === "none" ? "" : v)
                    }
                  >
                    <SelectTrigger
                      className="bg-card border-border"
                      data-testid={`away-goal-assist-${index}`}
                    >
                      <SelectValue placeholder="Assist (optional)" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="none">No Assist</SelectItem>
                      {(presentAwayPlayers.length > 0 ? presentAwayPlayers : awayPlayers)
                        .filter((p) => p.id !== goal.player_id)
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.number ? `${p.number}. ` : ""}{p.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground w-16">
                      Minute
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="120"
                      value={goal.minute}
                      onChange={(e) =>
                        updateGoal(false, index, "minute", parseInt(e.target.value) || 0)
                      }
                      className="bg-card border-border w-20"
                      data-testid={`away-goal-minute-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No goals recorded
            </p>
          )}
        </div>
      </div>

      {/* Cards Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="font-heading text-lg font-bold uppercase">
              Cards
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={addCard}
            className="text-amber-500"
            data-testid="add-card-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Card
          </Button>
        </div>

        {cards.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((card, index) => (
              <div
                key={index}
                className="p-4 bg-background rounded-lg space-y-3"
                data-testid={`card-${index}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase text-muted-foreground">
                    Card {index + 1}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCard(index)}
                    className="text-destructive h-6 w-6 p-0"
                    data-testid={`remove-card-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <Select
                  value={card.player_id}
                  onValueChange={(v) => updateCard(index, "player_id", v)}
                >
                  <SelectTrigger
                    className="bg-card border-border"
                    data-testid={`card-player-${index}`}
                  >
                    <SelectValue placeholder="Select Player" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {presentHomePlayers.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          {homeTeam?.name}
                        </div>
                        {presentHomePlayers.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.number ? `${p.number}. ` : ""}{p.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {presentHomePlayers.length === 0 && homePlayers.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          {homeTeam?.name}
                        </div>
                        {homePlayers.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.number ? `${p.number}. ` : ""}{p.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {presentAwayPlayers.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          {awayTeam?.name}
                        </div>
                        {presentAwayPlayers.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.number ? `${p.number}. ` : ""}{p.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {presentAwayPlayers.length === 0 && awayPlayers.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          {awayTeam?.name}
                        </div>
                        {awayPlayers.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.number ? `${p.number}. ` : ""}{p.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Select
                    value={card.card_type}
                    onValueChange={(v) => updateCard(index, "card_type", v)}
                  >
                    <SelectTrigger
                      className="bg-card border-border flex-1"
                      data-testid={`card-type-${index}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="yellow">Yellow</SelectItem>
                      <SelectItem value="red">Red</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    max="120"
                    value={card.minute}
                    onChange={(e) =>
                      updateCard(index, "minute", parseInt(e.target.value) || 0)
                    }
                    placeholder="Min"
                    className="bg-card border-border w-20"
                    data-testid={`card-minute-${index}`}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            No cards issued
          </p>
        )}
      </div>

      {/* Referee Assignment Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-bold uppercase">
              Referee Assignments
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={addRefereeAssignment}
            className="text-primary"
            data-testid="add-referee-assignment-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Referee
          </Button>
        </div>

        {/* Venue Input */}
        <div className="mb-4">
          <Label className="text-xs text-muted-foreground">Match Venue</Label>
          <Input
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            placeholder="e.g., Main Stadium, Field A"
            className="bg-background border-border mt-1"
            data-testid="venue-input"
          />
        </div>

        {refereeAssignments.length > 0 ? (
          <div className="space-y-4">
            {refereeAssignments.map((assignment, index) => {
              const filteredOfficials = getFilteredOfficials(assignment.role);
              return (
                <div
                  key={index}
                  className="p-4 bg-background rounded-lg space-y-3"
                  data-testid={`referee-assignment-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase text-muted-foreground">
                      Official {index + 1}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRefereeAssignment(index)}
                      className="text-destructive h-6 w-6 p-0"
                      data-testid={`remove-referee-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {/* STEP 1: Select Role First */}
                    <Select
                      value={assignment.role || "none"}
                      onValueChange={(v) => updateRefereeAssignment(index, "role", v === "none" ? "" : v)}
                    >
                      <SelectTrigger
                        className="bg-card border-border"
                        data-testid={`role-select-${index}`}
                      >
                        <SelectValue placeholder="1. Select Role" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="none">Select Role</SelectItem>
                        <SelectItem value="Center Referee">Center Referee</SelectItem>
                        <SelectItem value="Assistant">Assistant</SelectItem>
                        <SelectItem value="Delegate">Delegate</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* STEP 2: Select Official (filtered by role) */}
                    <Select
                      value={assignment.referee_id || "none"}
                      onValueChange={(v) => updateRefereeAssignment(index, "referee_id", v === "none" ? "" : v)}
                      disabled={!assignment.role}
                    >
                      <SelectTrigger
                        className="bg-card border-border"
                        data-testid={`referee-select-${index}`}
                      >
                        <SelectValue placeholder={assignment.role ? "2. Select Official" : "Select role first"} />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="none">Select Official</SelectItem>
                        {filteredOfficials.length === 0 ? (
                          <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                            {assignment.role === "Delegate" 
                              ? "No delegates available. Mark players as delegates in the Officials tab."
                              : "No referees available. Mark players as referees in the Officials tab."}
                          </div>
                        ) : (
                          filteredOfficials.map((ref) => {
                            const refTeam = teams.find(t => t.id === ref.team_id);
                            return (
                              <SelectItem key={ref.id} value={ref.id}>
                                {ref.name} ({refTeam?.name || 'Unknown'})
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
            
            {/* Assign & Notify Button */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={() => handleAssignReferees(false)}
                disabled={saving || refereeAssignments.filter(a => a.referee_id).length === 0}
                className="flex-1"
                data-testid="assign-without-notify-btn"
              >
                Assign Only
              </Button>
              <Button
                onClick={() => handleAssignReferees(true)}
                disabled={saving || refereeAssignments.filter(a => a.referee_id).length === 0}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="assign-and-notify-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Assign & Notify
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <UserCheck className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              No referees assigned yet
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Click "Add Referee" to assign officials to this match
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          variant="secondary"
          onClick={() => handleSave(false)}
          disabled={saving}
          className="flex-1"
          data-testid="save-draft-btn"
        >
          Save Draft
        </Button>
        <Button
          onClick={() => handleSave(true)}
          disabled={saving}
          className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
          data-testid="complete-match-btn"
        >
          <Check className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Complete Match"}
        </Button>
      </div>
    </div>
  );
}
